import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, Button, Input, Modal } from '@besiktning/ui';
import { garageApi, carCareApi, adminApi, AD_TEMPLATES } from '@besiktning/ui';
import type { Ad, AdTemplateKey } from '@besiktning/ui';
import { Plus, Pencil, Trash2, Megaphone, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { t } from '../i18n';

// ─── Template rendering ────────────────────────────────────────────────────────

function getTemplate(key: AdTemplateKey | string) {
  return AD_TEMPLATES.find((t) => t.key === key) ?? AD_TEMPLATES[0];
}

function AdCard({
  title,
  description,
  cta_text,
  template_key,
  className = '',
}: {
  title: string;
  description?: string | null;
  cta_text?: string | null;
  template_key: AdTemplateKey | string;
  className?: string;
}) {
  const tpl = getTemplate(template_key);
  return (
    <div
      className={`relative overflow-hidden rounded-xl ${className}`}
      style={{
        background: `linear-gradient(135deg, ${tpl.gradient[0]}, ${tpl.gradient[1]})`,
        minHeight: 140,
      }}
    >
      {/* Decorative circles */}
      <div
        className="absolute -right-6 -top-6 h-28 w-28 rounded-full opacity-15"
        style={{ background: tpl.accentBg }}
      />
      <div
        className="absolute -bottom-4 -left-4 h-20 w-20 rounded-full opacity-10"
        style={{ background: tpl.accentBg }}
      />

      <div className="relative flex h-full flex-col justify-end p-4">
        <p
          className="text-base font-bold leading-tight"
          style={{ color: tpl.textColor }}
        >
          {title || t('ads.headingFallback')}
        </p>
        {description && (
          <p
            className="mt-1 text-xs opacity-80 line-clamp-2"
            style={{ color: tpl.textColor }}
          >
            {description}
          </p>
        )}
        {cta_text && (
          <div
            className="mt-2 self-start rounded-full px-3 py-1 text-xs font-semibold"
            style={{ background: tpl.accentBg, color: tpl.accentText }}
          >
            {cta_text}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Form state ────────────────────────────────────────────────────────────────

interface AdFormState {
  title: string;
  description: string;
  cta_text: string;
  cta_url: string;
  template_key: AdTemplateKey;
  target_kommun: string;
  start_date: string;
  end_date: string;
  active: boolean;
  priority: string;
}

const today = new Date().toISOString().split('T')[0];
const oneMonthLater = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
  .toISOString()
  .split('T')[0];

const initialForm: AdFormState = {
  title: '',
  description: '',
  cta_text: '',
  cta_url: '',
  template_key: 'brand',
  target_kommun: '',
  start_date: today,
  end_date: oneMonthLater,
  active: true,
  priority: '0',
};

function isLive(ad: Ad): boolean {
  return ad.active && ad.start_date <= today && ad.end_date >= today;
}

// ─── Screen ────────────────────────────────────────────────────────────────────

export default function Ads() {
  const queryClient = useQueryClient();
  const { currentMembership, user } = useAuthStore();
  const isSuperadmin = user?.is_superadmin === true;
  const isGarage = currentMembership?.type === 'garage';
  const businessApi = isGarage ? garageApi : carCareApi;

  const [tab] = useState<'business' | 'promoted'>('business');
  const isPromotedTab = tab === 'promoted' && isSuperadmin;
  const activeApi = isPromotedTab ? adminApi : businessApi;

  const [showModal, setShowModal] = useState(false);
  const [editingAd, setEditingAd] = useState<Ad | null>(null);
  const [form, setForm] = useState<AdFormState>(initialForm);

  const { data: ads = [], isLoading } = useQuery({
    queryKey: ['ads', isPromotedTab ? 'promoted' : currentMembership?.type, currentMembership?.id],
    queryFn: () => activeApi.getAds(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof activeApi.createAd>[0]) => activeApi.createAd(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ads'] }); closeModal(); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof activeApi.updateAd>[1] }) =>
      activeApi.updateAd(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ads'] }); closeModal(); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => activeApi.deleteAd(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ads'] }),
  });

  const openCreate = () => { setEditingAd(null); setForm(initialForm); setShowModal(true); };

  const openEdit = (ad: Ad) => {
    setEditingAd(ad);
    setForm({
      title: ad.title,
      description: ad.description ?? '',
      cta_text: ad.cta_text ?? '',
      cta_url: ad.cta_url ?? '',
      template_key: ad.template_key,
      target_kommun: ad.target_kommun ?? '',
      start_date: ad.start_date,
      end_date: ad.end_date,
      active: ad.active,
      priority: ad.priority.toString(),
    });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingAd(null); setForm(initialForm); };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data = {
      title: form.title,
      description: form.description || undefined,
      cta_text: form.cta_text || undefined,
      cta_url: form.cta_url || undefined,
      template_key: form.template_key,
      ...(isPromotedTab && { target_kommun: form.target_kommun || undefined }),
      start_date: form.start_date,
      end_date: form.end_date,
      active: form.active,
      priority: parseInt(form.priority) || 0,
    };
    if (editingAd) {
      updateMutation.mutate({ id: editingAd.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const liveCount = ads.filter(isLive).length;
  const scheduledCount = ads.filter((a) => !isLive(a) && a.active && a.start_date > today).length;
  const inactiveCount = ads.filter((a) => !a.active).length;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('ads.pageTitle')}</h1>
          <p className="text-gray-600">{t('ads.pageSubtitle')}</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          {t('ads.newAd')}
        </Button>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4">
          <p className="text-sm text-gray-600">{t('ads.statActiveNow')}</p>
          <p className="text-2xl font-bold text-green-600">{liveCount}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-sm text-gray-600">{t('ads.statScheduled')}</p>
          <p className="text-2xl font-bold text-blue-600">{scheduledCount}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-sm text-gray-600">{t('ads.statInactive')}</p>
          <p className="text-2xl font-bold text-gray-400">{inactiveCount}</p>
        </CardContent></Card>
      </div>

      {/* Ads carousel */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : ads.length === 0 ? (
        <div className="rounded-xl bg-gray-50 py-16 text-center">
          <Megaphone className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">{t('ads.emptyTitle')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('ads.emptyDescription')}
          </p>
          <Button onClick={openCreate} className="mt-4">
            <Plus className="mr-2 h-4 w-4" />
            {t('ads.createFirst')}
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Horizontal scroll with peek */}
          <div className="overflow-x-auto pb-4 -mx-1 px-1 snap-x snap-mandatory scroll-smooth" style={{ scrollbarWidth: 'thin' }}>
            <div className="flex gap-4" style={{ width: 'max-content' }}>
              {ads.map((ad) => {
                const live = isLive(ad);
                return (
                  <div key={ad.id} className="snap-start shrink-0" style={{ width: 'clamp(280px, 40vw, 360px)' }}>
                    <Card className={`overflow-hidden h-full ${!ad.active ? 'opacity-60' : ''}`}>
                      {/* Template preview */}
                      <AdCard
                        title={ad.title}
                        description={ad.description}
                        cta_text={ad.cta_text}
                        template_key={ad.template_key}
                        className="rounded-none"
                      />

                      <CardContent className="p-4">
                        {/* Status + dates */}
                        <div className="mb-3 flex items-center justify-between">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            live ? 'bg-green-100 text-green-700' :
                            ad.active && ad.start_date > today ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {live ? t('ads.statusLive') : ad.active && ad.start_date > today ? t('ads.statusScheduled') : t('ads.statusInactive')}
                          </span>
                          <span className="text-xs text-gray-400">
                            {ad.start_date} → {ad.end_date}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(ad)}>
                            <Pencil className="mr-1 h-4 w-4" /> {t('ads.edit')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            title={ad.active ? t('ads.deactivate') : t('ads.activate')}
                            onClick={() => updateMutation.mutate({ id: ad.id, data: { active: !ad.active } })}
                          >
                            {ad.active
                              ? <ToggleRight className="h-4 w-4 text-green-600" />
                              : <ToggleLeft className="h-4 w-4 text-gray-400" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => deleteMutation.mutate(ad.id)}
                            isLoading={deleteMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingAd ? t('ads.modalTitleEdit') : t('ads.modalTitleNew')}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={closeModal}>{t('ads.cancel')}</Button>
            <Button
              onClick={handleSubmit}
              isLoading={createMutation.isPending || updateMutation.isPending}
              disabled={!form.title}
            >
              {editingAd ? t('ads.saveChanges') : t('ads.createAd')}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Live preview */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">{t('ads.preview')}</p>
            <AdCard
              title={form.title || t('ads.headingFallback')}
              description={form.description || undefined}
              cta_text={form.cta_text || undefined}
              template_key={form.template_key}
            />
          </div>

          {/* Template picker */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">{t('ads.template')}</p>
            <div className="grid grid-cols-3 gap-2">
              {AD_TEMPLATES.map((t) => {
                const selected = form.template_key === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setForm({ ...form, template_key: t.key })}
                    className={`relative overflow-hidden rounded-lg p-3 text-left transition-all ${
                      selected ? 'ring-2 ring-blue-500 ring-offset-1' : 'ring-1 ring-gray-200 hover:ring-gray-300'
                    }`}
                    style={{
                      background: `linear-gradient(135deg, ${t.gradient[0]}, ${t.gradient[1]})`,
                    }}
                  >
                    <span
                      className="text-xs font-semibold"
                      style={{ color: t.textColor }}
                    >
                      {t.name}
                    </span>
                    {selected && (
                      <div
                        className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full text-white"
                        style={{ background: t.accentBg, color: t.accentText }}
                      >
                        ✓
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Text fields */}
          <Input
            label={t('ads.titleLabel')}
            placeholder={t('ads.titlePlaceholder')}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{t('ads.descriptionLabel')}</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t('ads.descriptionPlaceholder')}
              rows={2}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('ads.ctaTextLabel')}
              placeholder={t('ads.ctaTextPlaceholder')}
              value={form.cta_text}
              onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
            />
            <Input
              label={t('ads.ctaUrlLabel')}
              placeholder="https://..."
              value={form.cta_url}
              onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('ads.startDateLabel')}
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              required
            />
            <Input
              label={t('ads.endDateLabel')}
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              required
            />
          </div>

          <div className="flex items-center gap-3">
            <Input
              label={t('ads.priorityLabel')}
              type="number"
              placeholder="0"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="w-32"
            />
            <div className="flex items-center gap-2 pt-5">
              <input
                type="checkbox"
                id="ad-active"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="ad-active" className="text-sm text-gray-700">{t('ads.adIsActive')}</label>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
